import { Controller } from "@hotwired/stimulus"

/**
 * Lead Quality by Geography — metric toggle (Avg quality vs. Lead count).
 *
 * Defaults to "quality" because the card is titled "Lead Quality by
 * Geography" — the count view is the alternate, not the primary.
 *
 * Both metrics ship their full bin payload + per-metric legend on the
 * partial. Switching metrics swaps:
 *   - the geo-chart `data-geo-chart-url-value` (data: URI carrying
 *     the chosen JSON payload — no server round trip)
 *   - the geo-chart `data-geo-chart-bins-value` (so the colorAxis
 *     re-bins to the active metric's scale; otherwise count data
 *     against quality bins paints everything dark blue)
 *   - which legend (`qualityLegend` / `countLegend`) is visible
 *
 * Targets:
 *   metric        — the toggle buttons (data-metric="count|quality")
 *   map           — the geo-chart container element
 *   qualityLegend — wrapper around the avg-quality discrete legend
 *   countLegend   — wrapper around the lead-count discrete legend
 *
 * Values:
 *   storageKey    — localStorage key (default
 *                   "reports.geography.quality_metric")
 *   defaultMetric — fallback when nothing is in localStorage
 *                   (default "quality" — matches the card title)
 */
export default class extends Controller {
  static targets = ["metric", "map", "qualityLegend", "countLegend"]
  static values  = {
    storageKey:    { type: String, default: "reports.geography.quality_metric" },
    defaultMetric: { type: String, default: "quality" }
  }

  connect() {
    const stored = this._safeRead(this.storageKeyValue)
    const initial = stored || this.defaultMetricValue || "quality"
    this._applyMetric(initial)
  }

  select(event) {
    const metric = event.currentTarget.dataset.metric
    if (!metric) return

    this._safeWrite(this.storageKeyValue, metric)
    this._applyMetric(metric)
  }

  _applyMetric(metric) {
    // Pressed state on the toggle buttons.
    this.metricTargets.forEach(btn => {
      const active = btn.dataset.metric === metric
      btn.classList.toggle("btn-secondary", active)
      btn.classList.toggle("btn-outline-secondary", !active)
      btn.setAttribute("aria-pressed", active ? "true" : "false")
    })

    // Legend visibility — show only the legend matching the active
    // metric. Use inline display so we don't depend on a global CSS
    // rule that could be tree-shaken at build time.
    if (this.hasQualityLegendTarget) {
      this.qualityLegendTarget.style.display = metric === "quality" ? "" : "none"
    }
    if (this.hasCountLegendTarget) {
      this.countLegendTarget.style.display = metric === "count" ? "" : "none"
    }

    if (!this.hasMapTarget) return

    // Swap the bins payload first so the geo-chart's binsValueChanged
    // callback queues a re-draw against the new scale, then swap the
    // data URL so urlValueChanged kicks the actual re-fetch. Stimulus
    // batches mutations in the same microtask; the geo-chart child
    // observes both values and re-draws once.
    const binsKey = metric === "quality" ? "geographyQualityBinsQualityValue" : "geographyQualityBinsCountValue"
    const newBins = this.mapTarget.dataset[binsKey]
    if (newBins) {
      this.mapTarget.dataset.geoChartBinsValue = newBins
    }

    const payloadKey = metric === "quality" ? "geographyQualityPayloadQualityValue" : "geographyQualityPayloadCountValue"
    const payload = this.mapTarget.dataset[payloadKey]
    if (!payload) return

    // Use a data: URI so geo-chart re-fetches without round-tripping
    // the server. encodeURIComponent keeps the JSON intact through URL
    // parsing.
    this.mapTarget.dataset.geoChartUrlValue = `data:application/json,${encodeURIComponent(payload)}`
  }

  _safeRead(key) {
    try { return window.localStorage.getItem(key) } catch (e) { return null }
  }

  _safeWrite(key, value) {
    try { window.localStorage.setItem(key, value) } catch (e) { /* private mode */ }
  }
}
